import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { FiArrowLeft, FiCalendar, FiChevronDown } from 'react-icons/fi';
import { interviewService } from '../services/interviewService';
import { getUserFacingError, UserFacingErrorKind } from '../services/http';
import { useInterview } from '../context/InterviewContext';
import ScoreCard from '../components/interview/ScoreCard';
import StatusBadge from '../components/interview/StatusBadge';
import { resolveInterviewTimestampMeta } from '../utils/interviewTimestamp';

interface QAItem {
  id: number;
  question_text: string;
  question_type: 'intro' | 'main' | 'followup' | 'candidate_question';
  category: string;
  question_index: number | null;
  answer: {
    id: number;
    answer_text: string;
    score: number | null;
    ai_feedback: string;
    time_spent_seconds: number;
  } | null;
}

interface DetailJobMeta {
  title?: string | null;
  name?: string | null;
  job_title?: string | null;
  jobTitle?: string | null;
  job_name?: string | null;
  jobName?: string | null;
}

interface DetailApplicationMeta {
  position?: string | null;
  job?: DetailJobMeta | null;
  job_title?: string | null;
  jobTitle?: string | null;
  job_name?: string | null;
  jobName?: string | null;
}

interface DetailResult {
  interview: {
    id: number;
    status: string;
    created_at?: string | null;
    started_at?: string | null;
    completed_at: string | null;
    job?: DetailJobMeta | null;
    application?: DetailApplicationMeta | null;
    job_title?: string | null;
    jobTitle?: string | null;
    job_name?: string | null;
    jobName?: string | null;
  };
  application?: DetailApplicationMeta | null;
  job?: DetailJobMeta | null;
  questions_with_answers: QAItem[];
  result: {
    final_score: number;
    technical_score: number;
    communication_score: number;
    problem_solving_score: number;
    status: 'strong' | 'average' | 'weak';
    summary: string;
    strength: string;
    weakness: string;
  } | null;
}

type DetailViewState = 'loading' | 'ready' | 'empty' | 'unauthorized' | 'not_found' | 'error';

const categoryLabel: Record<string, string> = {
  technical: 'Teknikal',
  communication: 'Komunikasi',
  problem_solving: 'Problem Solving',
};

const resolveQuestionTypeLabel = (item: QAItem, fallbackIndex: number) => {
  if (item.question_type === 'intro') return 'Perkenalan diri';
  if (item.question_type === 'candidate_question') return 'Pertanyaan kandidat';
  if (item.question_type === 'followup') return 'Follow-up';
  return `Pertanyaan ${item.question_index ?? fallbackIndex + 1}`;
};

const getCleanLabel = (value: unknown) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const resolveDetailJobTitle = (detail: DetailResult) => {
  const candidates = [
    detail.interview.job?.title,
    detail.interview.job?.name,
    detail.interview.job?.job_title,
    detail.interview.job?.jobTitle,
    detail.interview.job?.job_name,
    detail.interview.job?.jobName,
    detail.interview.job_title,
    detail.interview.jobTitle,
    detail.interview.job_name,
    detail.interview.jobName,
    detail.interview.application?.job?.title,
    detail.interview.application?.job?.name,
    detail.interview.application?.position,
    detail.interview.application?.job_title,
    detail.interview.application?.jobTitle,
    detail.application?.job?.title,
    detail.application?.job?.name,
    detail.application?.position,
    detail.application?.job_title,
    detail.application?.jobTitle,
    detail.job?.title,
    detail.job?.name,
  ];

  for (const candidate of candidates) {
    const label = getCleanLabel(candidate);
    if (label) return label;
  }

  return 'Posisi tidak tersedia';
};

const resolveScoreTone = (score: number) => {
  if (score >= 80) return 'strong';
  if (score >= 60) return 'average';
  return 'weak';
};

const ANSWER_PREVIEW_CHARACTER_THRESHOLD = 260;

const InterviewDetail: React.FC = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { interviewData, bindInterviewScope } = useInterview();

  const scopedJobId = Number(state?.jobId ?? interviewData.jobId ?? 0);
  const scopedOutsourceId = Number(state?.outsourceId ?? interviewData.outsourceId ?? 0);

  const interviewId: number = state?.interviewId ?? interviewData.interviewId ?? 0;

  const [detail, setDetail] = useState<DetailResult | null>(null);
  const [viewState, setViewState] = useState<DetailViewState>('loading');
  const [error, setError] = useState('');
  const [errorRetryable, setErrorRetryable] = useState(false);
  const [activeItemId, setActiveItemId] = useState<number | null>(null);

  useEffect(() => {
    if (!scopedJobId || !scopedOutsourceId) return;
    bindInterviewScope({ jobId: scopedJobId, outsourceId: scopedOutsourceId });
  }, [bindInterviewScope, scopedJobId, scopedOutsourceId]);

  const applyErrorState = (kind: UserFacingErrorKind, message: string) => {
    setErrorRetryable(kind === 'network' || kind === 'server' || kind === 'unknown');
    if (kind === 'auth') {
      setViewState('unauthorized');
      setError(message);
      return;
    }
    if (kind === 'not_found') {
      setViewState('not_found');
      setError(message);
      return;
    }
    setViewState('error');
    setError(message);
  };

  const loadDetail = async () => {
    if (!interviewId) {
      setViewState('empty');
      setError('Sesi interview tidak ditemukan.');
      return;
    }

    setViewState('loading');
    setError('');
    try {
      const res = await interviewService.getDetail(interviewId);
      const nextDetail = res.data?.data ?? null;
      if (!nextDetail) {
        setDetail(null);
        setViewState('empty');
        return;
      }
      setDetail(nextDetail);
      setViewState('ready');
    } catch (err) {
      const userFacingError = getUserFacingError(err, 'Gagal memuat detail interview.');
      applyErrorState(userFacingError.kind, userFacingError.message);
    }
  };

  useEffect(() => {
    loadDetail();
  }, [interviewId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setActiveItemId(null);
  }, [interviewId]);

  if (viewState === 'loading') {
    return (
      <div className="iv-detail iv-detail--loading">
        <Spinner animation="border" />
        <p>Memuat detail...</p>
      </div>
    );
  }

  if (viewState === 'unauthorized') {
    return (
      <div className="iv-detail iv-detail--error">
        <p>{error || 'Sesi login Anda sudah berakhir.'}</p>
        <button className="iv-btn iv-btn--outline" onClick={() => navigate('/login')}>
          Login Lagi
        </button>
      </div>
    );
  }

  if (viewState === 'not_found') {
    return (
      <div className="iv-detail iv-detail--error">
        <p>{error || 'Detail interview tidak ditemukan.'}</p>
        <button
          type="button"
          className="iv-detail__state-btn"
          onClick={() => navigate(-1)}
          aria-label="Kembali ke halaman sebelumnya"
        >
          <FiArrowLeft aria-hidden="true" />
          <span>Kembali</span>
        </button>
      </div>
    );
  }

  if (viewState === 'empty' || !detail) {
    return (
      <div className="iv-detail iv-detail--error">
        <p>Detail interview belum tersedia.</p>
        <button
          type="button"
          className="iv-detail__state-btn"
          onClick={() => navigate(-1)}
          aria-label="Kembali ke halaman sebelumnya"
        >
          <FiArrowLeft aria-hidden="true" />
          <span>Kembali</span>
        </button>
      </div>
    );
  }

  if (viewState === 'error') {
    return (
      <div className="iv-detail iv-detail--error">
        <p>{error || 'Gagal memuat detail interview.'}</p>
        {errorRetryable ? (
          <button className="iv-btn iv-btn--outline" onClick={loadDetail}>
            Coba Lagi
          </button>
        ) : (
          <button
            type="button"
            className="iv-detail__state-btn"
            onClick={() => navigate(-1)}
            aria-label="Kembali ke halaman sebelumnya"
          >
            <FiArrowLeft aria-hidden="true" />
            <span>Kembali</span>
          </button>
        )}
      </div>
    );
  }

  const interviewTimestamp = resolveInterviewTimestampMeta(detail.interview);
  const hasResult = Boolean(detail.result);
  const jobTitle = resolveDetailJobTitle(detail);

  return (
    <div className="iv-detail">
      <div className="iv-detail__page">
        <header className="iv-detail__header">
          <button
            type="button"
            className="iv-detail__back-btn"
            onClick={() => navigate(-1)}
            aria-label="Kembali ke halaman sebelumnya"
          >
            <FiArrowLeft aria-hidden="true" />
            <span>Kembali</span>
          </button>

          <div className="iv-detail__headline">
            <div className="iv-detail__title-row">
              <h1 className="iv-detail__title">Detail Jawaban Interview</h1>
              <span className="iv-detail__job-badge" title={jobTitle}>{jobTitle}</span>
            </div>
            {interviewTimestamp ? (
              <div className="iv-detail__timestamp">
                <FiCalendar aria-hidden="true" />
                <span>{interviewTimestamp.label}: <strong>{interviewTimestamp.display}</strong></span>
              </div>
            ) : null}
          </div>
        </header>

        <div className={`iv-detail__layout${hasResult ? '' : ' iv-detail__layout--single'}`}>
          {detail.result ? (
            <aside className="iv-detail__summary-rail">
              <div className="iv-detail__summary-card">
                <span className="iv-detail__summary-eyebrow">Hasil keseluruhan</span>
                <div className="iv-detail__summary-top">
                  <div className="iv-detail__summary-score-block">
                    <span className="iv-detail__final-score">{detail.result.final_score}</span>
                    <span className="iv-detail__final-score-label">Skor akhir</span>
                  </div>
                  <StatusBadge status={detail.result.status} />
                </div>
                <div className="iv-detail__score-breakdown">
                  <ScoreCard label="Teknikal" score={detail.result.technical_score} />
                  <ScoreCard
                    label="Problem Solving"
                    score={detail.result.problem_solving_score}
                  />
                  <ScoreCard label="Komunikasi" score={detail.result.communication_score} />
                </div>
                {detail.result.summary ? (
                  <p className="iv-detail__summary-text">{detail.result.summary}</p>
                ) : null}
              </div>
            </aside>
          ) : null}

          <section className="iv-detail__content">
            <div className="iv-detail__qa-list">
              {detail.questions_with_answers.map((item, idx) => {
                const isExpanded = activeItemId === item.id;
                const hasAnswer = Boolean(item.answer);
                const showToggle = Boolean(
                  item.answer &&
                    (item.answer.answer_text.length > ANSWER_PREVIEW_CHARACTER_THRESHOLD ||
                      item.answer.ai_feedback),
                );

                return (
                  <div
                    key={item.id}
                    className={`iv-detail__qa-item${isExpanded ? ' iv-detail__qa-item--open' : ''}`}
                  >
                    <div className="iv-detail__qa-meta">
                      <span className={`iv-detail__qa-type iv-detail__qa-type--${item.question_type}`}>
                        {resolveQuestionTypeLabel(item, idx)}
                      </span>
                      <span className="iv-detail__qa-category">
                        {categoryLabel[item.category] ?? item.category}
                      </span>
                      {item.answer && item.answer.score !== null && item.question_type !== 'candidate_question' ? (
                        <span
                          className={`iv-detail__qa-score iv-detail__qa-score--${resolveScoreTone(Number(item.answer.score))}`}
                        >
                          {item.answer.score} / 100
                        </span>
                      ) : item.answer && item.question_type === 'candidate_question' ? (
                        <span className="iv-detail__qa-score iv-detail__qa-score--neutral">
                          Tidak dinilai
                        </span>
                      ) : null}
                    </div>

                    <p className="iv-detail__question-text">{item.question_text}</p>

                    {hasAnswer && item.answer ? (
                      <div className="iv-detail__answer-wrap">
                        <div
                          className={`iv-detail__answer-preview${
                            isExpanded || !showToggle ? ' is-open' : ''
                          }`}
                        >
                          <p className="iv-detail__answer-text">{item.answer.answer_text}</p>
                        </div>

                        {showToggle ? (
                          <button
                            type="button"
                            className="iv-detail__expand-btn"
                            aria-expanded={isExpanded}
                            onClick={() =>
                              setActiveItemId((current) => (current === item.id ? null : item.id))
                            }
                          >
                            <span>{isExpanded ? 'Tutup detail' : 'Lihat jawaban lengkap'}</span>
                            <FiChevronDown
                              className={`iv-detail__expand-icon${isExpanded ? ' is-open' : ''}`}
                              aria-hidden="true"
                            />
                          </button>
                        ) : null}

                        {isExpanded && item.answer.ai_feedback ? (
                          <div className="iv-detail__feedback">
                            <strong>Feedback AI</strong>
                            <p>{item.answer.ai_feedback}</p>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <p className="iv-detail__no-answer">Belum dijawab</p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default InterviewDetail;
